/**
 * O ENDEREÇO do relatório comparativo — `/relatorio?c=<id>,<id>&obj=<objetivo>` (lote de
 * 09/10/2026, item 7). Puro, sem DOM (testado em `tests/relatorio-endereco.test.ts`).
 *
 * O relatório abre em ABA PRÓPRIA, sem a casca: imprime limpo (sem barra lateral nem a
 * superior, que tem backdrop-filter) e o endereço pode ser copiado e mandado. Por isso ele
 * é uma rota fora do roteador da casca (`rotaDe` descarta a query): `main.ts` desvia para a
 * página ANTES de criar a casca, e a página lê `location.search` por aqui.
 *
 * ⚠ O ENDEREÇO É EDITÁVEL. A tela só oferece 2 a 5 cenários publicados do mesmo
 * empreendimento, mas quem abre o link pode ter mexido nele — e o Admin Hype enxerga todas
 * as incorporadoras. O que dá para conferir sem o banco é conferido AQUI (formato do id,
 * quantidade, repetição, objetivo); o resto (mesma empresa, mesmo empreendimento, visível,
 * publicado, ativo) a página confere com o que o banco devolve.
 */
import { CONSTANTES } from './frase';
import { objetivoPorChave } from './textos';
import type { ChaveObjetivo } from './textos';

/** Só ids do banco. Os cenários "fantasma" do simulador (o "Otimizado" local) são `c12`. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function ehUuid(s: unknown): s is string {
  return typeof s === 'string' && UUID.test(s);
}

export const CAMINHO_RELATORIO = '/relatorio';

export function enderecoDoRelatorio(ids: readonly string[], objetivo: ChaveObjetivo): string {
  return `${CAMINHO_RELATORIO}?c=${ids.map(encodeURIComponent).join(',')}&obj=${encodeURIComponent(objetivo)}`;
}

export type PedidoRelatorio =
  | { ok: true; ids: string[]; objetivo: ChaveObjetivo }
  | { ok: false; erro: string };

/** Lê e confere o que dá para conferir sem o banco. A frase do erro é a que a página mostra. */
export function lerPedidoDoRelatorio(search: string): PedidoRelatorio {
  const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const brutos = (q.get('c') ?? '').split(',').map((s) => s.trim()).filter((s) => s !== '');
  const obj = objetivoPorChave(q.get('obj'));
  if (brutos.some((s) => !ehUuid(s))) {
    return { ok: false, erro: 'O endereço do relatório tem um cenário que não existe no banco. Gere o relatório de novo pelo botão "Relatório comparativo".' };
  }
  const ids = [...new Set(brutos.map((s) => s.toLowerCase()))];
  if (ids.length !== brutos.length) {
    return { ok: false, erro: 'O endereço do relatório repete um cenário. Gere o relatório de novo pelo botão "Relatório comparativo".' };
  }
  if (ids.length < CONSTANTES.minCenarios || ids.length > CONSTANTES.maxCenarios) {
    return { ok: false, erro: 'O relatório compara de 2 a 5 cenários. Gere o relatório de novo pelo botão "Relatório comparativo".' };
  }
  if (obj === null) {
    return { ok: false, erro: 'O endereço do relatório não diz qual é o objetivo da comparação. Gere o relatório de novo pelo botão "Relatório comparativo".' };
  }
  return { ok: true, ids, objetivo: obj.k };
}

/**
 * O que a página confere com o que o banco devolveu. Devolve a frase do erro, ou `null`.
 * Separado para ser testado sem banco: recebe os cenários já abertos (a RLS já filtrou —
 * quem não enxerga um deles nem chega aqui, `abrirCenario` lança antes).
 */
export function conferirCenariosDoRelatorio(
  cenarios: ReadonlyArray<{ nome: string; empresaId: string; empreendimentoId: string; versaoPublicada: number; arquivadoEm: string | null }>,
): string | null {
  const primeiro = cenarios[0];
  if (primeiro === undefined) return 'Nenhum cenário do relatório pôde ser aberto.';
  if (cenarios.some((c) => c.empresaId !== primeiro.empresaId)) {
    return 'O relatório só compara cenários de UMA incorporadora, e este endereço mistura incorporadoras.';
  }
  if (cenarios.some((c) => c.empreendimentoId !== primeiro.empreendimentoId)) {
    return 'O relatório só compara cenários do MESMO empreendimento, e este endereço mistura empreendimentos.';
  }
  const arquivado = cenarios.find((c) => c.arquivadoEm !== null);
  if (arquivado !== undefined) return `O cenário "${arquivado.nome}" está arquivado: restaure-o para incluí-lo no relatório.`;
  const nunca = cenarios.find((c) => c.versaoPublicada <= 0);
  if (nunca !== undefined) return `O cenário "${nunca.nome}" nunca foi publicado: o relatório usa a versão publicada de cada cenário.`;
  return null;
}
